<script setup lang="ts">
import DroneSvg from '@/assets/icons/drone_white_icon.svg'
import { i18n } from '@/i18n'
import { useCountdownRing } from '@/hooks/useCountdownRing'

const $t = i18n.global.t

// 「航拍还在飞」的接续条。
//
// ── 它替掉的是什么 ──────────────────────────────────────────────
//
// 原来：轮询命中 → router.replace 直接换屏。上一秒还是打卡点列表，下一秒
// 整屏变成航拍界面，零过渡零解释。用户的心理模型是「我被踢走了」，
// 不是「我被接回去了」。
//
// 现在：先浮这一条，说清楚「有一次航拍还在飞、已经飞了多久」，给 4 秒
// 让他读完 —— 这 4 秒是**看得见**的，边框在退，他知道它会自己走、还剩多少。
// 想早点回去直接撳。
//
// ── 刻意只有一个状态 ────────────────────────────────────────────
//
// 第一版做了三档（还在飞 / 已结束 / 已中断），实机一验就错：航拍明明正在
// 进行，条上写的是「上次航拍已结束」—— 那套 orderStatus 映射是猜的。
// Zack：「先不加这么多状态，我怕出错。」
//
// 所以现在只认一个信号：findliveStreamPushedByTicketId 命中。那是原来硬跳
// 用的同一个判据，**被生产验证过**。没命中就什么都不显示，退回今天的行为，
// 不多猜一个字。
const props = defineProps<{
  /** 「02:14」。拿不到就不显示时长，只说「航拍进行中」 */
  elapsedText?: string
}>()

const emit = defineEmits<{
  go: []
  dismiss: []
}>()

/**
 * 自动回到航拍页的时长。
 *
 * 4 秒：够读完一句话并决定要不要早点撳，又不至于让一个正在飞的航程干等。
 * 原来是 0 秒（硬跳），这 4 秒买的是「知道发生了什么」。
 */
const AUTO_GO = 4
const RING_STROKE = 2

const rootRef = ref<HTMLElement | null>(null)
const ringEl = ref<SVGRectElement | null>(null)
const ring = useCountdownRing(rootRef, ringEl, RING_STROKE)

let gsapLib: any = null
let ringTween: any = null
let mm: { revert: () => void } | null = null
let leaving = false

const text = computed(() =>
  props.elapsedText
    ? $t('aerialPage.resume.flyingWithTime', { time: props.elapsedText })
    : $t('aerialPage.resume.flying'),
)

const leave = () => {
  if (leaving) return
  leaving = true
  ringTween?.kill()
  ringTween = null
  const root = rootRef.value
  if (!root || !gsapLib) {
    emit('dismiss')
    return
  }
  gsapLib.killTweensOf(root.querySelectorAll('*'))
  // 往上飞走 —— 和「回到那件事上去」同向。0.22s 比页面切换短，两个动作叠在
  // 一起读起来是一件事，而不是「条先关掉，然后页面再变」两件事。
  gsapLib.to(root, {
    y: -14,
    autoAlpha: 0,
    scale: 0.94,
    duration: 0.22,
    ease: 'power2.in',
    onComplete: () => emit('dismiss'),
  })
}

const go = () => {
  emit('go')
  leave()
}

const play = async () => {
  const { gsap } = await import('gsap')
  gsapLib = gsap
  const root = rootRef.value
  if (!root || leaving) return

  ringTween = ring.start(gsap, AUTO_GO, go)

  const media = gsap.matchMedia()
  media.add(
    {
      normal: '(prefers-reduced-motion: no-preference)',
      reduced: '(prefers-reduced-motion: reduce)',
    },
    (ctx) => {
      if (ctx.conditions?.reduced) {
        gsap.set(root, { autoAlpha: 1, y: 0, scale: 1 })
        return
      }
      gsap.from(root, {
        y: 16,
        scale: 0.92,
        autoAlpha: 0,
        duration: 0.5,
        ease: 'back.out(1.5)',
      })
      // ── 无人机：搬的是旧浮窗（home-flight-entry）那一套 ──────────
      //
      // Zack：「我偏向新的，但旧的无人机动态更好。」那套确实好 —— 它不是
      // 装饰，是在说「这台机器**此刻正在天上**」。所以这里破例用循环：
      // 循环信号本身容易被脱敏，但这条只活 4 秒，而且「还在飞」本来就是
      // 一个持续中的事实，用持续的动作表达它是对的。
      //
      // 三条的周期刻意都不一样（1.6 / 2.3 / 1.8+1.1），合起来不会同步成
      // 机械的节拍 —— 这是旧那版就定好的，照搬。
      const icon = root.querySelector('.resume-bar__icon')
      const wash = root.querySelector('.resume-bar__downwash')

      // 起飞：跟着整条一起进场
      gsap.from(icon, {
        y: 8,
        scale: 0.7,
        autoAlpha: 0,
        duration: 0.5,
        ease: 'back.out(2.2)',
        delay: 0.1,
      })
      // 悬停：上下浮
      gsap.to(icon, {
        y: -3,
        duration: 1.6,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      })
      // 侧摆
      gsap.to(icon, {
        rotation: 3,
        duration: 2.3,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      })
      // 旋翼下洗：一圈涟漪往外扩散
      gsap.fromTo(
        wash,
        { scale: 0.85, autoAlpha: 0.45 },
        {
          scale: 1.7,
          autoAlpha: 0,
          duration: 1.8,
          ease: 'power2.out',
          repeat: -1,
          repeatDelay: 1.1,
          delay: 0.4,
        },
      )
    },
  )
  mm = media
}

onMounted(play)
onBeforeUnmount(() => {
  ringTween?.kill()
  ringTween = null
  mm?.revert()
  mm = null
})
</script>

<template>
  <div
    ref="rootRef"
    class="resume-bar"
    role="button"
    @click="go"
  >
    <!-- 边框即倒计时：4 秒退完就自动回航拍页。
         初始 hidden，量到几何才显形 —— 否则首帧会闪一整圈实线。 -->
    <svg
      class="resume-bar__ring"
      aria-hidden="true"
    >
      <rect
        ref="ringEl"
        fill="none"
      />
    </svg>

    <!-- 无人机 + 旋翼下洗，不是箭头。
         上一版左边用的是 arrow_up（从「离你更近」那条抄来的，那里「上」指的是
         上面那张卡），放这里没有意义；右边那个本意是关闭钮的 X，11px 在真机上
         就是读成「›」，和文案连起来更像「点这里前进」。两个都拿掉了。
         整块相对定位，涟漪压在机身底下。 -->
    <span class="resume-bar__drone">
      <span
        class="resume-bar__downwash"
        aria-hidden="true"
      />
      <drone-svg class="resume-bar__icon" />
    </span>
    <span class="resume-bar__text">{{ text }}</span>
    <span class="resume-bar__action">{{ $t('aerialPage.resume.backToFlight') }}</span>
  </div>
</template>

<style scoped lang="scss">
.resume-bar {
  // 和「XXX 离你更近」同一个槽位、同一套观感 —— 同一类东西（浮在底部、
  // 可撳、会自己走）就该长得一样，用户不用重新学。
  position: fixed;
  left: 50%;
  bottom: calc(#{$home-bar-height} + 2rem);
  transform: translateX(-50%);
  z-index: 1200;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  box-sizing: border-box;
  // ⚠️ fixed + left:50% 的收缩宽度只按「从 50% 到右边缘」那半屏算，
  // 不写 max-content 的话药丸最宽只有半屏，文案会无故被省略号切掉。
  width: max-content;
  max-width: calc(100% - 3.2rem);
  padding: 1.1rem 1.8rem;
  border-radius: 4rem;
  background: linear-gradient(180deg, #64c7ff 0%, #ffffff 100%);
  color: $primary-black-color;
  box-shadow:
    0 0.8rem 2.4rem rgba(0, 0, 0, 0.45),
    0 0 1.6rem rgba(100, 199, 255, 0.3);
  overflow: hidden;

  &__ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: visible;

    rect {
      visibility: hidden;
      stroke: $primary-blue-color;
      stroke-width: 2;
      stroke-linecap: round;
    }
  }

  &__drone {
    position: relative;
    flex: 0 0 auto;
    width: 2.2rem;
    height: 2.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // 旋翼下洗：和机身同尺寸的一圈涟漪，由 gsap 放大 + 淡出，压在机身底下。
  // 初始状态交给 gsap 的 fromTo；这里先藏好，免得动效加载前闪一圈实线。
  &__downwash {
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    border-radius: 50%;
    border: 0.2rem solid $primary-blue-color;
    opacity: 0;
    pointer-events: none;
  }

  &__icon {
    position: relative;
    width: 100%;
    height: 100%;
    color: $primary-black-color;

    // 素材写死 fill="white"，在浅底上等于看不见，跟着 color 走
    :deep(path) {
      fill: currentColor;
    }
  }

  &__text {
    position: relative;
    min-width: 0;
    font-size: 1.5rem;
    line-height: 2.1rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__action {
    position: relative;
    flex: 0 0 auto;
    color: $primary-blue-color;
    font-size: 1.5rem;
    line-height: 2.1rem;
    font-weight: 700;
    padding-left: 0.8rem;
    border-left: 0.1rem solid rgba(29, 34, 46, 0.25);
  }
}
</style>
